import { Event, EventTarget } from 'event-target-shim';

export enum StoreStatus {
  Pending = 'pending', // empty store, pending for hydration (or if not persisted, pending for initilization or shutdown)
  Hydrated = 'hydrated', // finished hydration, readable, pending for initilization or shutdown
  Initializing = 'initializing', // initialization started
  Ready = 'ready', // initialization finished
  ShuttingDown = 'shuttingDown', // shutdown started
  Terminated = 'terminated', // shutdown finished
}

class ReadyEvent extends Event {
  readonly isReady: boolean;

  constructor(isReady: boolean) {
    super('ready');
    this.isReady = isReady;
  }
}

export class StoreLifecycle<
  I extends unknown[] = [],
  S extends unknown[] = [],
> extends EventTarget<{
  ready: ReadyEvent;
  hydrated: Event;
}> {
  readonly name: string;
  status: StoreStatus;
  readonly hydratedPromise: Promise<void>;
  private readyPromise: Promise<boolean> | null;
  private cleanup: void | (() => void);

  constructor(name: string) {
    super();
    this.name = name;
    this.status = StoreStatus.Pending;
    this.hydratedPromise = new Promise((resolve) => {
      this.addEventListener(
        'hydrated',
        () => {
          resolve();
        },
        { once: true },
      );
    });
    this.readyPromise = new Promise((resolve) => {
      this.addEventListener(
        'ready',
        (event) => {
          resolve(event.isReady);
        },
        { once: true },
      );
    });
    this.cleanup = undefined;
  }

  getStatus() {
    return this.status;
  }

  finishHydration() {
    if (this.status !== StoreStatus.Pending) {
      throw new Error(`${this.name} already hydrated`);
    }

    this.status = StoreStatus.Hydrated;
    this.dispatchEvent(new Event('hydrated'));
  }

  createInitialize(
    initialize: (
      ...args: I
    ) => void | (() => void) | Promise<void> | Promise<() => void>,
    notifyStatusChange: () => void,
  ): (...args: I) => Promise<void> {
    return async (...args: I) => {
      await this.hydratedPromise;
      if (
        ![StoreStatus.Hydrated, StoreStatus.Terminated].includes(this.status)
      ) {
        throw new Error(`${this.name} already initialized`);
      }
      const originalStatus = this.status;
      this.status = StoreStatus.Initializing;
      notifyStatusChange();
      if (!this.readyPromise) {
        this.readyPromise = new Promise((resolve) => {
          this.addEventListener(
            'ready',
            (event) => {
              resolve(event.isReady);
            },
            { once: true },
          );
        });
      }

      try {
        this.cleanup = await initialize(...args);
        this.status = StoreStatus.Ready;
        notifyStatusChange();
        this.readyPromise = null;
        this.dispatchEvent(new ReadyEvent(true));
      } catch (err) {
        this.status = originalStatus;
        notifyStatusChange();
        throw err;
      }
    };
  }

  createShutdown(
    shutdown: (...args: S) => void | Promise<void>,
    notifyStatusChange: () => void,
  ): (...args: S) => Promise<void> {
    return async (...args: S) => {
      await this.hydratedPromise;
      if (![StoreStatus.Hydrated, StoreStatus.Ready].includes(this.status)) {
        throw new Error(`${this.name} already shutdown`);
      }

      const originalStatus = this.status;
      this.status = StoreStatus.ShuttingDown;
      notifyStatusChange();

      try {
        // cleanup first, last or pass into shutdown?
        this.cleanup?.();
        this.cleanup = undefined;
        await shutdown(...args);
        this.status = StoreStatus.Terminated;
        notifyStatusChange();
        this.readyPromise = null;
        this.dispatchEvent(new ReadyEvent(false));
      } catch (err) {
        this.status = originalStatus;
        notifyStatusChange();
        throw err;
      }
    };
  }

  ifReady<A extends unknown[]>(
    f: (...args: A) => void | Promise<void>,
  ): (...args: A) => Promise<void> {
    return async (...args) => {
      if (this.readyPromise) {
        await this.readyPromise;
      }
      if (this.status === StoreStatus.Ready) {
        await f(...args);
      }
    };
  }

  ifReadyElseThrow<A extends unknown[], R>(
    f: (...args: A) => R | Promise<R>,
  ): (...args: A) => Promise<R> {
    return async (...args) => {
      if (this.readyPromise) {
        await this.readyPromise;
      }
      if (this.status === StoreStatus.Ready) {
        return f(...args);
      }

      throw new Error(`${this.name} not ready`);
    };
  }
}

export interface StoreLifecycleAction<
  I extends unknown[] = [],
  S extends unknown[] = [],
> {
  initialize: (...params: I) => Promise<void>;
  shutdown: (...params: S) => Promise<void>;
}
